import { useEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay/hooks";

import type {
  useOrganizationResourceGroups_resourceGroups$data,
  useOrganizationResourceGroups_resourceGroups$key,
} from "__generated__/useOrganizationResourceGroups_resourceGroups.graphql";
import type { useOrganizationResourceGroupsQuery } from "__generated__/useOrganizationResourceGroupsQuery.graphql";
import type { ConnectionNodeType } from "components/common/Pagination";

const resourceGroupsFragment = graphql`
  fragment useOrganizationResourceGroups_resourceGroups on Organization
  @argumentDefinitions(cursor: { type: "String" }, count: { type: "Int", defaultValue: 10 })
  @refetchable(queryName: "useOrganizationResourceGroupsRefetchQuery") {
    resourceGroups(first: $count, after: $cursor)
      @connection(key: "useOrganizationResourceGroups_resourceGroups") {
      edges {
        node {
          id
          uuid
          name
          isOrganizationDefault
        }
      }
    }
  }
`;

const query = graphql`
  query useOrganizationResourceGroupsQuery($orgUuid: String!, $cursor: String, $count: Int!) {
    organization(uuid: $orgUuid) {
      ...useOrganizationResourceGroups_resourceGroups @arguments(cursor: $cursor, count: $count)
    }
  }
`;

export type ResourceGroup = ConnectionNodeType<
  useOrganizationResourceGroups_resourceGroups$data["resourceGroups"]
>;

interface UseOrganizationResourceGroupsProps {
  orgUuid: string;
  initialCount?: number;
  batchCount?: number;
}

interface UseOrganizationResourceGroupsReturn {
  resourceGroups: ResourceGroup[];
  isLoading: boolean;
}

// hook to fetch all resource groups for an organization
export const useOrganizationResourceGroups = ({
  orgUuid,
  initialCount = 100,
  batchCount = 100,
}: UseOrganizationResourceGroupsProps): UseOrganizationResourceGroupsReturn => {
  const [resourceGroups, setResourceGroups] = useState<ResourceGroup[]>([]);
  const resourceGroupsQuery = useLazyLoadQuery<useOrganizationResourceGroupsQuery>(query, {
    orgUuid,
    count: initialCount,
  });

  const paginationQuery = usePaginationFragment<
    useOrganizationResourceGroupsQuery,
    useOrganizationResourceGroups_resourceGroups$key
  >(resourceGroupsFragment, resourceGroupsQuery.organization);

  useEffect(() => {
    if (paginationQuery.hasNext) {
      paginationQuery.loadNext(batchCount);
    }
  }, [paginationQuery, batchCount]);

  useMemo(() => {
    const newResourceGroups =
      paginationQuery.data?.resourceGroups?.edges?.map(edge => edge?.node!) ?? [];
    setResourceGroups(newResourceGroups);
  }, [paginationQuery.data]);

  return { resourceGroups, isLoading: paginationQuery.isLoadingNext };
};

export const useDefaultOrganizationResourceGroup = (orgUuid: string): ResourceGroup | null => {
  const orgResourceGroups = useOrganizationResourceGroups({ orgUuid });
  const defaultRg = orgResourceGroups.resourceGroups.find(rg => rg.isOrganizationDefault);
  return defaultRg ?? null;
};
